import { useEffect, useMemo, useRef, useState } from 'react';
import { collection, limit as fsLimit, onSnapshot, query, where } from 'firebase/firestore';
import { ChevronDown, ChevronRight, MessageSquare } from 'lucide-react';
import { db } from '../lib/firebase';
import { useMessages } from '../hooks/useMessages';
import type { Conversation, Message } from '../lib/types';

// Stacked past-conversation cards shown above the current thread (Freshchat-style):
// oldest on top, each a FULL-WIDTH collapsed row with channel, created date, and
// status. Clicking a card EXPANDS that conversation's messages INLINE (read-only)
// in the same window — it never navigates to a different conversation view. The
// current conversation renders expanded below (ChatHeader + MessageThread).

const CHANNEL_LABEL: Record<string, string> = {
  whatsapp: 'WhatsApp',
  web: 'Web',
  email: 'Email',
  sms: 'SMS',
  facebook: 'Facebook',
  api: 'API',
};

// Mirror the main thread's bubble palette so an expanded past conversation looks
// consistent (read-only — no actions/edit).
const BUBBLE: Record<Message['type'], string> = {
  customer: 'bg-white text-gray-900 border border-gray-200',
  agent: 'bg-primary-600 text-white',
  ai: 'bg-violet-50 text-violet-900 border border-violet-200',
  system: 'bg-amber-50 text-amber-900 border border-amber-200',
};
const ALIGN: Record<Message['type'], string> = {
  customer: 'justify-start',
  agent: 'justify-end',
  ai: 'justify-end',
  system: 'justify-center',
};

function statusBadge(s: string): string {
  if (s === 'resolved') return 'bg-green-100 text-green-700';
  if (s === 'pending') return 'bg-amber-100 text-amber-700';
  if (s === 'snoozed') return 'bg-purple-100 text-purple-700';
  return 'bg-blue-100 text-blue-700';
}

function fmtDate(ms: number): string {
  if (!ms) return '';
  try {
    return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return '';
  }
}

// A single collapsed/expandable past conversation. Loads its own messages ONLY
// while expanded (useMessages(null) is a no-op), so the stack stays cheap.
function StackedCard({ chat }: { chat: Conversation }) {
  const [open, setOpen] = useState(false);
  const { messages, loading } = useMessages(open ? chat.id : null);
  const visible = messages.filter((m) => !m.internal);

  return (
    <div className="bg-white">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-4 py-3 text-left hover:bg-gray-50"
      >
        <div className="min-w-0 flex items-start gap-2">
          {open ? (
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-sm font-medium text-gray-800 truncate">
              <MessageSquare className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
              {CHANNEL_LABEL[chat.channel] ?? chat.channel}
              {chat.contactPhone && (
                <span className="text-gray-400 font-normal" dir="ltr">· {chat.contactPhone}</span>
              )}
            </div>
            <div className="text-[11px] text-gray-500 mt-0.5 truncate">
              Created {fmtDate(chat.createdAt)}
              {chat.lastMessagePreview && (
                <span className="text-gray-400"> · {chat.lastMessagePreview.slice(0, 60)}</span>
              )}
            </div>
          </div>
        </div>
        <span
          className={`text-[10px] font-medium uppercase px-2 py-0.5 rounded flex-shrink-0 ${statusBadge(chat.status)}`}
        >
          {chat.status}
        </span>
      </button>

      {open && (
        // Inset the expanded past conversation so it reads as a SELF-CONTAINED
        // bounded card (tinted bg + ring), clearly distinct from the live thread.
        <div className="bg-gray-100 border-t border-gray-200 px-2 py-2">
          <div className="rounded-lg ring-1 ring-gray-200 bg-gray-50 px-3 py-2 space-y-1.5">
            {loading && <div className="text-xs text-gray-400 text-center py-2">Loading…</div>}
            {!loading && visible.length === 0 && (
              <div className="text-xs text-gray-400 text-center py-2">No messages in this conversation.</div>
            )}
            {visible.map((m) => (
              <div key={m.id} className={`flex ${ALIGN[m.type] ?? 'justify-start'}`}>
                <div
                  className={`${BUBBLE[m.type] ?? BUBBLE.customer} rounded-lg px-3 py-1.5 max-w-[80%] text-sm whitespace-pre-wrap break-words shadow-sm`}
                  dir="auto"
                >
                  {m.content}
                  {(m.attachmentPath || (m as { attachments?: unknown[] }).attachments?.length) && (
                    <span className="opacity-70"> 📎</span>
                  )}
                </div>
              </div>
            ))}
            {!loading && visible.length > 0 && (
              <div className="flex items-center gap-2 pt-1.5">
                <div className="flex-1 h-px bg-gray-300" />
                <span className="text-[10px] uppercase tracking-wide text-gray-400 flex-shrink-0">
                  End of {chat.status === 'resolved' ? 'resolved' : 'this'} conversation
                </span>
                <div className="flex-1 h-px bg-gray-300" />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function ConversationHistoryStack({
  contactId,
  contactEmail,
  contactPhone,
  currentChatId,
  onLoaded,
}: {
  contactId: string;
  /** Same-contact match also keys on a shared email/phone, not just contactId —
   *  identified web chats get a per-session anon contactId but share the email,
   *  so a contactId-only match misses the contact's other conversations. */
  contactEmail?: string;
  contactPhone?: string;
  currentChatId: string;
  /** Called after the stack's height settles so the parent can re-pin to bottom. */
  onLoaded?: () => void;
}) {
  const [byId, setById] = useState<Conversation[]>([]);
  const [byEmail, setByEmail] = useState<Conversation[]>([]);
  const [byPhone, setByPhone] = useState<Conversation[]>([]);

  // Three single-field equality queries (each auto-indexed); merged + sorted
  // client-side to avoid a composite index. Each stream replaces its own slice.
  const mapDocs = (snap: { docs: { id: string; data: () => unknown }[] }): Conversation[] =>
    snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Conversation, 'id'>) }));

  useEffect(() => {
    if (!contactId) { setById([]); return; }
    const q = query(collection(db, 'chats'), where('contactId', '==', contactId), fsLimit(30));
    return onSnapshot(q, (snap) => setById(mapDocs(snap)), () => {});
  }, [contactId]);

  useEffect(() => {
    const email = contactEmail?.trim().toLowerCase();
    if (!email) { setByEmail([]); return; }
    const q = query(collection(db, 'chats'), where('contactEmail', '==', email), fsLimit(30));
    return onSnapshot(q, (snap) => setByEmail(mapDocs(snap)), () => {});
  }, [contactEmail]);

  useEffect(() => {
    const phone = contactPhone?.trim();
    if (!phone) { setByPhone([]); return; }
    const q = query(collection(db, 'chats'), where('contactPhone', '==', phone), fsLimit(30));
    return onSnapshot(q, (snap) => setByPhone(mapDocs(snap)), () => {});
  }, [contactPhone]);

  const past = useMemo(() => {
    const m = new Map<string, Conversation>();
    for (const c of [...byId, ...byEmail, ...byPhone]) m.set(c.id, c);
    return [...m.values()]
      // Drop the current chat and merged-away shells (their messages moved out).
      .filter((c) => c.id !== currentChatId && !(c as { mergedInto?: string }).mergedInto)
      .sort((a, b) => (a.createdAt ?? 0) - (b.createdAt ?? 0));
  }, [byId, byEmail, byPhone, currentChatId]);

  // Re-pin the parent scroll once the cards (which add height above the thread)
  // first render. Ref-held so prop churn doesn't refire it; keyed on count so it
  // does NOT refire when a user expands a card (which would scroll them away).
  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  useEffect(() => {
    onLoadedRef.current?.();
  }, [past.length]);

  if (past.length === 0) return null;

  return (
    <div>
      <div className="divide-y divide-gray-200 border-y border-gray-200">
        {past.map((c) => (
          <StackedCard key={c.id} chat={c} />
        ))}
      </div>
      {/* Clear boundary: everything above is PAST history; the live thread starts
          below this marker. */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-gray-50">
        <div className="flex-1 h-px bg-gray-300" />
        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 flex-shrink-0">
          Current conversation
        </span>
        <div className="flex-1 h-px bg-gray-300" />
      </div>
    </div>
  );
}
